import { registerToolDefinition, type ToolDefinition, type TurnAccess } from '@edge/edge-ai'
import type { GraphApi } from '@/features/backtest/graph-api/types'
import {
  GRAPH_READ_METHODS,
  GRAPH_MUTATION_METHODS,
  dispatchGraphMethod,
} from '@/features/backtest/graph-api/method-catalog'

export const GRAPH_EDIT_METHODS = new Set<string>([
  'addNode',
  'setParams',
  'connect',
  'setStaticInput',
  'setInputField',
  'removeNode',
  'clearGraph',
  'attachDebugChart',
  'groupNodes',
  'setGroupCollapsed',
  'dissolveGroup',
  'exposeParam',
  'unexposeParam',
  'tidy',
  'organizeNodes',
  'movePaneToMain',
  'movePaneToOwnPane',
  'focusOnPrice',
  'unfocusFromPrice',
])

export const GRAPH_TOOL_NAMES = new Set([
  'graph_read',
  'graph_call',
  'analyze_backtest',
  'read_current_graph',
  'get_node_specs',
  'read_backtest_results',
  'rerun_backtest',
  'add_node',
  'connect_nodes',
  'set_node_parameters',
  'remove_node',
  'create_strategy_template',
  'create_strategy_from_recipe',
  'apply_graph_ops',
])

/** Saved graph tools use the same access policy but never require an editor context. */
export const SAVED_STRATEGY_TOOL_NAMES = new Set([
  'create_saved_strategy',
  'read_saved_strategy',
  'edit_saved_strategy',
  'run_saved_strategy',
])

/**
 * The turn's access decides (2026-10-15, one-assistant Phase 2b), on every plan
 * since Build went away (2026-10-16, Phase 2d): a signed-in turn the turn check
 * read as a change request may edit; changes that ask first still wait for the
 * user's OK.
 */
export function assertGraphToolAccess(
  readOnly: boolean,
  turn: { access: TurnAccess; authenticated: boolean },
): void {
  if (!turn.authenticated) throw new Error('Sign in to access backtest tools.')
  if (!readOnly && turn.access !== 'change') {
    throw new Error('Graph actions need a turn the user asked for a change in.')
  }
}

for (const [name, methods] of [
  ['graph_read', GRAPH_READ_METHODS],
  ['graph_call', GRAPH_MUTATION_METHODS],
] as const) {
  const definition: ToolDefinition = {
    name,
    description: `${name === 'graph_read' ? 'Inspect' : 'Operate'} the active authenticated strategy editor. Call graph_read help for signatures. args is the ordered argument array. No strategy deletion or batch execution. Lifecycle actions may persist changes; run returns an execution receipt.`,
    input_schema: {
      type: 'object',
      properties: {
        method: { type: 'string', enum: [...methods] },
        args: { type: 'array', items: {}, maxItems: 4 },
      },
      required: ['method', 'args'],
      additionalProperties: false,
    },
  }
  registerToolDefinition(definition)
}

export async function executeGraphMethod(
  api: GraphApi,
  tool: 'graph_read' | 'graph_call',
  input: Record<string, unknown>,
): Promise<unknown> {
  const { method, args } = input
  const allowed: readonly string[] =
    tool === 'graph_read' ? GRAPH_READ_METHODS : GRAPH_MUTATION_METHODS
  if (typeof method !== 'string' || !allowed.includes(method)) {
    throw new Error(`Method is unavailable through ${tool}.`)
  }
  if (!Array.isArray(args) || args.length > 4)
    throw new Error('args must be an ordered argument array.')
  const result = await dispatchGraphMethod(api, method, args)
  if (result && typeof result === 'object') {
    const receipt = result as Record<string, unknown>
    if (receipt.ok === false || receipt.error)
      throw new Error(String(receipt.error ?? 'Graph action rejected.'))
  }
  return result ?? null
}

/** Keep tool output bounded; never silently turn an oversized result into a sample. */
export function serializeGraphResult(result: unknown): string {
  const content = JSON.stringify(result, (_key, value) =>
    typeof value === 'bigint' ? value.toString() : value,
  )
  if (new TextEncoder().encode(content).byteLength > 128 * 1024) {
    throw new Error(
      'Result exceeds 128 KiB. Read individual nodes or use bounded analyze_backtest operations.',
    )
  }
  return content
}
